import React, { useEffect, useState, useCallback } from 'react';
import client from '../api/client';
import PeriodFilter, { calcRange } from '../components/PeriodFilter';
import PaginationFooter from '../components/PaginationFooter';

const THX_RED = '#B71530';

function fmt(val) {
  if (val == null) return '–';
  return `£${Number(val).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(s) {
  if (!s) return '–';
  try { return new Date(s).toLocaleDateString('en-GB'); } catch { return s; }
}


export default function CreditNotes() {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allocateModal, setAllocateModal] = useState(null);
  const [allocating, setAllocating] = useState(false);
  const [msg, setMsg] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [preset, setPreset] = useState('30');
  const [dateFrom, setDateFrom] = useState(() => calcRange('30').from);
  const [dateTo,   setDateTo]   = useState(() => calcRange('30').to);

  const fetchCreditNotes = useCallback(() => {
    setLoading(true);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo)   params.date_to   = dateTo;
    client.get('/credit-notes/', { params })
      .then(r => setCreditNotes(r.data.credit_notes || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchCreditNotes(); }, [fetchCreditNotes]);

  const handleAllocate = async () => {
    if (!allocateModal) return;
    setAllocating(true);
    try {
      await client.patch(`/credit-notes/${allocateModal.id}/allocate`, {});
      setMsg('Credit note marked as allocated');
      setAllocateModal(null);
      fetchCreditNotes();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to allocate');
    } finally {
      setAllocating(false);
    }
  };

  const handlePageSizeChange = (e) => { setPageSize(Number(e.target.value)); setPage(1); };

  const unallocated = creditNotes.filter(cn => !cn.allocated);
  const allocated = creditNotes.filter(cn => cn.allocated);
  const totalPages = Math.max(1, Math.ceil(creditNotes.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = creditNotes.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div>
      <div className="row g-3 mb-0">
        <div className="col-md-3">
          <div className="card text-center" style={{ borderLeft: `4px solid ${THX_RED}` }}>
            <div className="card-body py-3">
              <div style={{ fontSize: '26px', fontWeight: '700', color: THX_RED }}>{creditNotes.length}</div>
              <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase' }}>Total Credit Notes</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center" style={{ borderLeft: '4px solid #ffc107' }}>
            <div className="card-body py-3">
              <div style={{ fontSize: '26px', fontWeight: '700', color: '#ffc107' }}>{unallocated.length}</div>
              <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase' }}>Unallocated</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center" style={{ borderLeft: '4px solid #198754' }}>
            <div className="card-body py-3">
              <div style={{ fontSize: '26px', fontWeight: '700', color: '#198754' }}>{allocated.length}</div>
              <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase' }}>Allocated</div>
            </div>
          </div>
        </div>
      </div>

      <PeriodFilter
        preset={preset} dateFrom={dateFrom} dateTo={dateTo}
        onPresetChange={d => { const r = calcRange(d); setPreset(d); setDateFrom(r.from); setDateTo(r.to); setPage(1); }}
        onFromChange={e => { setDateFrom(e.target.value); setPreset('custom'); setPage(1); }}
        onToChange={e => { setDateTo(e.target.value); setPreset('custom'); setPage(1); }}
      />

      {msg && <div className="alert alert-info py-2 mb-3" style={{ fontSize: '14px' }}>{msg}</div>}

      <div className="card">
        <div className="card-header py-2">
          <span style={{ fontSize: '14px', fontWeight: '600' }}>Credit Notes ({creditNotes.length})</span>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-danger spinner-border-sm" /></div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm mb-0" style={{ fontSize: '13px' }}>
                <thead className="table-light">
                  <tr>
                    <th>Supplier</th>
                    <th>Credit Note #</th>
                    <th>Date</th>
                    <th className="text-end">Amount</th>
                    <th>Currency</th>
                    <th>Reference Invoice</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 && (
                    <tr><td colSpan="8" className="text-center text-muted py-4">No credit notes found</td></tr>
                  )}
                  {paginated.map(cn => (
                    <tr key={cn.id}>
                      <td>{cn.supplier_canonical_name || cn.supplier_name || '–'}</td>
                      <td>{cn.credit_note_number || '–'}</td>
                      <td>{fmtDate(cn.credit_date)}</td>
                      <td className="text-end">{fmt(cn.gross_amount)}</td>
                      <td>{cn.currency || 'GBP'}</td>
                      <td>{cn.reference_invoice || '–'}</td>
                      <td>
                        <span className={`badge ${cn.allocated ? 'bg-success' : 'bg-warning text-dark'}`}>
                          {cn.allocated ? 'Allocated' : 'Unallocated'}
                        </span>
                      </td>
                      <td>
                        {!cn.allocated && (
                          <button
                            className="btn btn-sm"
                            style={{ backgroundColor: THX_RED, color: '#fff', border: 'none', fontSize: '11px', padding: '2px 8px' }}
                            onClick={() => setAllocateModal(cn)}
                          >
                            Allocate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {!loading && creditNotes.length > 0 && (
          <PaginationFooter
            page={currentPage}
            totalPages={totalPages}
            total={creditNotes.length}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>

      {allocateModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ fontSize: '16px' }}>Allocate Credit Note</h5>
                <button type="button" className="close" onClick={() => setAllocateModal(null)}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ fontSize: '14px' }}>
                <p><strong>Supplier:</strong> {allocateModal.supplier_canonical_name || allocateModal.supplier_name}</p>
                <p><strong>Credit Note #:</strong> {allocateModal.credit_note_number || '–'}</p>
                <p><strong>Amount:</strong> {fmt(allocateModal.gross_amount)}</p>
                <p>This will mark the credit note as allocated. Are you sure?</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setAllocateModal(null)}>Cancel</button>
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: THX_RED, color: '#fff', border: 'none' }}
                  onClick={handleAllocate}
                  disabled={allocating}
                >
                  {allocating ? 'Allocating...' : 'Confirm Allocation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
